import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from './ui/button';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null 
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so next render shows fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({ errorInfo });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/shops';
  };

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div 
          className="min-h-screen bg-[#0F172A] flex items-center justify-center p-6"
          data-testid="error-boundary-fallback"
        >
          <div className="max-w-md w-full bg-[#1E293B] border border-[#334155] rounded-xl p-8 text-center">
            {/* Error Icon */}
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#EF4444]/10 flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-[#EF4444]" />
            </div>

            {/* Error Title */}
            <h1 
              className="text-2xl font-bold text-[#E2E8F0] mb-2"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Something went wrong
            </h1>

            {/* Error Description */}
            <p className="text-[#94A3B8] mb-6">
              An unexpected error occurred. This might be due to a temporary issue or unexpected data from the server.
            </p>

            {/* Error Details (Development only) */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className="mb-6 p-4 bg-[#0F172A] rounded-lg text-left overflow-auto max-h-40">
                <p className="text-xs font-mono text-[#EF4444] break-words">
                  {this.state.error.toString()}
                </p>
                {this.state.errorInfo && (
                  <pre className="text-xs font-mono text-[#64748B] mt-2 whitespace-pre-wrap">
                    {this.state.errorInfo.componentStack?.slice(0, 500)}
                  </pre>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                onClick={this.handleRetry}
                className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
                data-testid="error-retry-button"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
              <Button
                onClick={this.handleGoHome}
                variant="outline"
                className="border-[#334155] text-[#94A3B8] hover:bg-[#334155] hover:text-[#E2E8F0]"
                data-testid="error-home-button"
              >
                <Home className="w-4 h-4 mr-2" />
                Go to Home
              </Button>
            </div>

            {/* Reload Option */}
            <p className="mt-6 text-sm text-[#64748B]">
              If the problem persists,{' '}
              <button
                onClick={this.handleReload}
                className="text-[#2563EB] hover:underline"
                data-testid="error-reload-link"
              >
                reload the page
              </button>
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
